import React, { useCallback } from "react"

import type { MetricIdentity } from "@/metrics/catalog"
import { Eye, EyeOff } from "lucide-react"

import type { MetricViews } from "@/types/MetricViewConfig"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

interface MetricViewsSectionProps {
  /** All valid metric IDs present in the run data. */
  metricIds: string[]
  metricRecordsById: Record<string, MetricIdentity>
  metricViews: MetricViews
  onMetricViewsChange: (metricViews: MetricViews) => void
}

/** Metric visibility toggle list, without any surrounding popover chrome. */
export const MetricViewsSection: React.FC<MetricViewsSectionProps> = ({
  metricIds,
  metricRecordsById,
  metricViews,
  onMetricViewsChange,
}) => {
  const toggleVisibility = useCallback(
    (metricId: string, visible: boolean) => {
      onMetricViewsChange({
        ...metricViews,
        [metricId]: { ...metricViews[metricId], visible },
      })
    },
    [metricViews, onMetricViewsChange],
  )

  return (
    <div className="flex flex-col max-h-72 overflow-y-auto overflow-x-hidden pb-1">
      {metricIds.map((metricId) => {
        const record = metricRecordsById[metricId]
        const displayName = resolveMetricDisplayName(
          record?.metric_display_name,
          record?.metric_name || metricId,
        )
        const entry = metricViews[metricId]
        const isVisible = entry?.visible ?? true

        return (
          <button
            key={metricId}
            type="button"
            onClick={() => toggleVisibility(metricId, !isVisible)}
            className={`flex items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-surface-1 cursor-pointer ${
              isVisible ? "text-coval-text-muted" : "text-coval-text-muted/40"
            }`}
            aria-label={
              isVisible ? `Hide ${displayName}` : `Show ${displayName}`
            }
          >
            {isVisible ? (
              <Eye className="h-3.5 w-3.5 shrink-0 text-coval-text-muted" />
            ) : (
              <EyeOff className="h-3.5 w-3.5 shrink-0 text-coval-text-muted/40" />
            )}
            <span className="text-sm truncate" title={displayName}>
              {displayName}
            </span>
          </button>
        )
      })}
    </div>
  )
}
